import React, { useEffect, useState } from 'react';
import { api } from '../../../services/api';
import { InventorySettings } from '../../../types';
import { supabase } from '../../../lib/supabase';
import { InventoryHistory } from './InventoryHistory';

const Toggle = ({ enabled, onChange, disabled = false }: { enabled: boolean; onChange: (v: boolean) => void; disabled?: boolean }) => (
  <button
    onClick={() => !disabled && onChange(!enabled)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
      enabled ? 'bg-blue-600' : 'bg-gray-200'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        enabled ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

export default function InventoryTab() {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [adminId, setAdminId] = useState<string | null>(null);

  // Removed autoDeleteVariant
  const [settings, setSettings] = useState<InventorySettings>({
    platform: 'shopify',
    isEnabled: false,
    autoDraftProduct: false,
    autoActiveProduct: false,
    hideVariantsCss: false,
    lowStockThreshold: 5
  });

  useEffect(() => {
    fetchSession();
  }, []);

  const fetchSession = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      setAdminId(session.user.id);
      loadSettings(session.user.id);
    }
  };

  const loadSettings = async (userId: string) => {
    try {
      const data = await api.getInventorySettings(userId);
      if (data) setSettings(data);
    } catch (err) {
      console.error("Failed to load settings", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = async (key: keyof InventorySettings, value: boolean) => {
    if (!adminId) return;
    setSettings(prev => ({ ...prev, [key]: value })); 
    try {
      await api.updateInventorySettings(adminId, { [key]: value });
    } catch (err) {
      setSettings(prev => ({ ...prev, [key]: !value }));
      alert("Failed to save setting");
    }
  };

  const handleCssToggle = async (value: boolean) => {
    if (!adminId || processing) return;
    setProcessing(true);
    try {
      await api.toggleHideVariantsCSS(adminId, value);
      await api.updateInventorySettings(adminId, { hideVariantsCss: value });
      setSettings(prev => ({ ...prev, hideVariantsCss: value }));
      alert(value ? "CSS Injected into Theme!" : "CSS Removed from Theme!");
    } catch (err: any) {
      alert(`Error: ${err.message}`);
      setSettings(prev => ({ ...prev, hideVariantsCss: !value }));
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading settings...</div>;

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div className="p-6 bg-white rounded-lg shadow-sm border border-gray-100">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800">Inventory Automation</h2>
          <p className="text-gray-500 mt-1">Manage how your store reacts to stock changes automatically.</p>
        </div>

        {/* Master Switch */}
        <div className="flex items-center justify-between p-5 bg-blue-50 rounded-xl mb-8 border border-blue-100">
          <div>
            <h3 className="font-bold text-lg text-blue-900">Enable Inventory Sync</h3>
            <p className="text-sm text-blue-700">Master switch for all inventory automations</p>
          </div>
          <Toggle 
            enabled={settings.isEnabled} 
            onChange={(v: boolean) => handleSettingChange('isEnabled', v)} 
          />
        </div>

        <div className={`space-y-8 ${!settings.isEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
          
          {/* Feature 1: Auto Draft */}
          <div className="flex items-start justify-between pb-6 border-b border-gray-100">
            <div>
              <h4 className="font-semibold text-gray-800 text-lg">1. Auto-Draft Out of Stock Products</h4>
              <p className="text-sm text-gray-500 mt-1 max-w-xl">
                When <strong>ALL</strong> variants reach 0 stock, the product status changes to 'Draft'.
              </p>
            </div>
            <Toggle 
              enabled={settings.autoDraftProduct} 
              onChange={(v: boolean) => handleSettingChange('autoDraftProduct', v)} 
            />
          </div>

          {/* Feature 2: Auto Publish */}
          <div className="flex items-start justify-between pb-6 border-b border-gray-100">
            <div>
              <h4 className="font-semibold text-gray-800 text-lg">2. Auto-Publish Restocked Products</h4>
              <p className="text-sm text-gray-500 mt-1 max-w-xl">
                When stock is added to a 'Draft' product, it automatically changes to 'Active'.
              </p>
            </div>
            <Toggle 
              enabled={settings.autoActiveProduct} 
              onChange={(v: boolean) => handleSettingChange('autoActiveProduct', v)} 
            />
          </div>

          {/* Feature 3: Hide Variants (CSS) */}
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-semibold text-gray-800 text-lg">3. Visual Hide (CSS Injection)</h4>
              <p className="text-sm text-gray-500 mt-1 max-w-xl">
                Injects code into your theme to visually hide buttons for out-of-stock variants.
                <span className="text-xs text-orange-600 font-bold bg-orange-50 px-2 ml-2 rounded inline-block">Note: Does not remove from Search</span>
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Toggle 
                enabled={settings.hideVariantsCss} 
                disabled={processing}
                onChange={handleCssToggle} 
              />
              {processing && <span className="text-xs text-blue-600 animate-pulse">Processing...</span>}
            </div>
          </div>

        </div>
      </div>

      {adminId && <InventoryHistory adminId={adminId} />}
    </div>
  );
}
